import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Text } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { IconAlertCircle, IconArrowLeft, IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "tecrmi_components/useAuth";
import { useSelectedOrganisation } from "xplatform_shell/useSelectedOrganisation";
import type { JobViewModel } from "@models/job";
import { useJobNotifications } from "@hooks/useJobNotifications";
import { useJobSubmit } from "@hooks/useJobSubmit";
import { useJobHistory } from "@hooks/useJobHistory";
import { useJobListFilter } from "@hooks/useJobListFilter";
import { useErrorToast } from "@hooks/useErrorToast";
import { useSuccessToast } from "@hooks/useSuccessToast";
import { useMappingJobActions } from "@hooks/mappingService/useMappingJobActions";
import JobFilterPanel from "@features/MappingService/JobFilterPanel";
import UploadDrawer from "@features/MappingService/UploadDrawer";
import UploadProgressBar from "@components/UploadProgressBar/UploadProgressBar";
import MappingServiceTopbar from "@features/MappingService/MappingServiceTopbar";
import JobListSection from "@features/MappingService/JobListSection";
import { MappingPluginId } from "@models/service";
import { parseServiceId } from "@utils/mappingServiceHelpers";
import { useInputTemplate } from "@hooks/useInputTemplate";
import "./MappingServicePage.scss";

/**
 * Rows a wd file may carry, mirrored from the backend's `MAX_ROWS_PER_FILE`.
 *
 * Stated here so the reviewer reads it BEFORE choosing a file: the backend
 * refuses an oversized sheet outright — no partial run, no cost — and finding
 * that out from a failed job is a wasted round trip. The two numbers are one
 * promise; if the backend cap moves, this moves with it.
 */
const WD_ROW_LIMIT = 300;

const MappingServicePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const parsed = useMemo(() => parseServiceId(serviceId), [serviceId]);
  const { user } = useAuth();
  const { organisation } = useSelectedOrganisation();
  const currentUserId = organisation?.userId ?? null;

  const { filter, applyFilter, isActive: filterActive, popover } =
    useJobListFilter({ id: organisation?.userId, email: user?.email });

  const upsertRef = useRef<(job: JobViewModel) => void>(() => {});
  const notify = useCallback((job: JobViewModel) => upsertRef.current(job), []);
  const { ensureConnected, subscribe } = useJobNotifications(notify);

  const {
    jobs,
    hasMore,
    isLoadingInitial,
    isLoadingMore,
    loadedOnce,
    error: historyError,
    clearError: clearHistoryError,
    upsertJob,
    loadMore,
    reload,
    deleteJob,
  } = useJobHistory({ parsed, filter, subscribe });
  useEffect(() => {
    upsertRef.current = upsertJob;
  }, [upsertJob]);

  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  // Above the invalid-service early return: hooks run in the same order on
  // every render, and an unparseable serviceId still renders a component.
  const template = useInputTemplate();

  const {
    submit,
    retry,
    isSubmitting,
    uploadProgress,
    lastUploadedFilename,
    clearLastUploaded,
    errorKey,
    errorText,
    failedUpload,
    clearError,
    dismissFailedUpload,
  } = useJobSubmit(ensureConnected, subscribe);

  const submitErrorMessage = errorKey
    ? t(`mappingServicePage.upload.errors.${errorKey}`)
    : errorText;

  useErrorToast(
    submitErrorMessage,
    t("mappingServicePage.upload.errorTitle"),
    clearError,
  );
  useSuccessToast(
    lastUploadedFilename
      ? t("mappingServicePage.upload.success", { filename: lastUploadedFilename })
      : null,
    t("mappingServicePage.upload.successTitle"),
    clearLastUploaded,
  );
  useErrorToast(
    historyError,
    t("mappingServicePage.jobs.errorTitle"),
    clearHistoryError,
  );

  const { handleDeleteJob, handleFilesSelected, handleRetry, openJob } =
    useMappingJobActions({
      parsed,
      serviceId,
      jobs,
      submit,
      retry,
      upsertJob,
      deleteJob,
    });

  useEffect(() => {
    if (!parsed) return;
    void ensureConnected().catch(() => {});
  }, [parsed, ensureConnected]);

  if (!parsed) {
    return (
      <Box className="mapping-service-page mapping-service-page--invalid">
        <Alert
          color="red"
          icon={<IconAlertCircle size={18} />}
          title={t("mappingServicePage.errors.invalidServiceTitle")}
        >
          <Text size="sm">{t("mappingServicePage.errors.invalidService")}</Text>
          <Button
            mt="md"
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(-1)}
          >
            {t("mappingServicePage.actions.backToDashboard")}
          </Button>
        </Alert>
      </Box>
    );
  }

  const { kind, pluginId } = parsed;
  // One flag, two things that are true only of the OE sheet: it has a row cap
  // worth stating up front, and it has a template whose headers must match
  // exactly. Both are about the file BEFORE it is chosen.
  const isOe = pluginId === MappingPluginId.WdMapping;
  const showsRowLimit = isOe;

  return (
    <Box className="mapping-service-page">
      <MappingServiceTopbar
        kind={kind}
        pluginId={pluginId}
        filterActive={filterActive}
        onBack={() => navigate(-1)}
        onOpenFilter={popover.openAt}
        onRefresh={reload}
        isLoadingInitial={isLoadingInitial}
        onDownloadTemplate={isOe ? template.download : undefined}
        templateDownloading={template.isDownloading}
        onOpenUpload={() => setUploadDrawerOpen(true)}
        uploadButtonLoading={isSubmitting && !uploadDrawerOpen}
      />

      <JobFilterPanel
        anchorEl={popover.anchorEl}
        open={popover.open}
        onClose={popover.close}
        onApply={applyFilter}
        initialFilter={filter}
      />

      <Box className="mapping-service-page__body">
        {showsRowLimit && (
          <Alert
            className="mapping-service-page__row-limit"
            color="yellow"
            icon={<IconInfoCircle size={18} />}
            title={t("mappingServicePage.rowLimitNotice.title")}
            mb="sm"
          >
            <Text size="sm">
              {t("mappingServicePage.rowLimitNotice.body", {
                limit: WD_ROW_LIMIT,
              })}
            </Text>
          </Alert>
        )}

        {uploadProgress && !uploadDrawerOpen && (
          <Box mb="sm">
            <UploadProgressBar uploadProgress={uploadProgress} />
          </Box>
        )}

        <UploadDrawer
          opened={uploadDrawerOpen}
          onClose={() => setUploadDrawerOpen(false)}
          busy={isSubmitting}
          uploadProgress={uploadProgress}
          onFilesSelected={(files) => void handleFilesSelected(files)}
          failedUpload={failedUpload}
          onRetry={() => void handleRetry()}
          onDismissFailed={dismissFailedUpload}
        />

        <JobListSection
          jobs={jobs}
          isLoadingInitial={isLoadingInitial}
          loadedOnce={loadedOnce}
          filterActive={filterActive}
          currentUserId={currentUserId}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onOpenJob={openJob}
          onDeleteJob={handleDeleteJob}
        />
      </Box>
    </Box>
  );
};

export default MappingServicePage;
